import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Fingerprint, KeyRound, LockKeyhole, ScrollText, ShieldCheck } from "lucide-react";
import { fetchAuthConfig } from "../../api/auth.js";
import type { SecondFactorOutcome } from "../../api/auth.js";
import { afterSecondFactor, chooseInitialPassword, describeAuthError, setupAdmin, signIn } from "../../app/authFlow.js";
import { passkeyUnlockAvailable, signInWithPasskey } from "../../app/passkeyUnlock.js";
import { paths } from "../../app/paths.js";
import { isDesktop } from "../../app/platform.js";
import { saveServerUrl, storedServerUrl } from "../../app/serverUrl.js";
import { apiBaseUrl } from "../../api/http.js";
import { normalizeServerUrl } from "../../lib/serverUrl.js";
import { MIN_MASTER_LENGTH, isValidEmail, masterPasswordProblem } from "../../lib/masterPassword.js";
import { useSession } from "../../app/session.js";
import { CryptoClient } from "../../cryptoClient.js";
import CryptoWorker from "../../workers/crypto.worker?worker";
import { Logo } from "../../ui/Logo.js";
import { Alert, Button } from "../../ui/primitives.js";
import { Checkbox, Field, SecretInput, StrengthMeter, TextInput } from "../../ui/forms.js";
import { NewPasswordFields } from "./NewPasswordFields.js";
import { SecondFactorStep } from "./SecondFactorStep.js";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { LINK_PATH } from "../../lib/extensionLink.js";

interface PendingStep {
  readonly kind: "second-factor" | "set-password";
  readonly client: CryptoClient;
  readonly email: string;
  readonly ticket: string;
  /** The one-time password just used; the new master password must differ from it. */
  readonly oneTime: string;
  /** The account was reset through admin recovery: its data stays, only the password is new. */
  readonly recovered: boolean;
  /** Length of the password just entered, for the policy check after unlock. */
  readonly passwordLength: number;
}

/** passwordLength is undefined after a passkey unlock: no password was typed. */
type OnAuthenticated = (client: CryptoClient, email: string, passwordLength: number | undefined) => void;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function createClient(): CryptoClient {
  return new CryptoClient(() => new CryptoWorker());
}

export function LoginPage(): ReactNode {
  useDocumentTitle("Anmelden");
  const { state, begin } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const desktop = isDesktop();
  const [server, setServer] = useState<string>((): string => (desktop ? storedServerUrl() ?? apiBaseUrl() : ""));
  const config = useQuery({ queryKey: ["auth", "config", server], queryFn: fetchAuthConfig, retry: false });
  const [pending, setPending] = useState<PendingStep | undefined>(undefined);
  const pendingRef = useRef<CryptoClient | undefined>(undefined);

  useEffect((): (() => void) => {
    return (): void => {
      pendingRef.current?.terminate();
      pendingRef.current = undefined;
    };
  }, []);

  const navState: unknown = location.state;
  const from = isRecord(navState) && typeof navState["from"] === "string" ? navState["from"] : paths.dashboard;

  // begin() re-renders this page before navigate(from) lands, so the guard must honor `from` too.
  if (state.status !== "locked") {
    return <Navigate to={from} replace />;
  }

  const idle = isRecord(navState) && navState["reason"] === "idle";
  // Linking the extension needs a passkey check anyway, so a passkey sign-in saves one prompt.
  const linking = from === LINK_PATH;
  const setupRequired = config.data?.setupRequired === true;
  const minLength = config.data?.minPasswordLength ?? MIN_MASTER_LENGTH;

  function onAuthenticated(client: CryptoClient, email: string, passwordLength: number | undefined): void {
    pendingRef.current = undefined;
    begin(email, client, passwordLength);
    navigate(from, { replace: true });
  }

  function onPending(step: PendingStep): void {
    pendingRef.current = step.client;
    setPending(step);
  }

  function cancelPending(): void {
    pendingRef.current?.terminate();
    pendingRef.current = undefined;
    setPending(undefined);
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <aside className="relative hidden overflow-hidden border-r border-line bg-ink-2 lg:flex">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: "linear-gradient(to right, #E9F4F1 1px, transparent 1px), linear-gradient(to bottom, #E9F4F1 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "linear-gradient(to bottom, black, transparent 85%)"
          }}
          aria-hidden="true"
        />
        <div className="absolute -left-40 -top-40 size-[34rem] rounded-full bg-flow/[0.07] blur-3xl" aria-hidden="true" />
        <div className="relative flex w-full flex-col justify-between p-10 xl:p-14">
          <Logo size={28} />
          <div className="max-w-lg">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-flow">Clientseitig verschlüsselter Passwortmanager</p>
            <h1 className="font-display mt-4 text-[2.6rem] font-extrabold leading-[1.02] tracking-[-0.025em] text-foam xl:text-[3.1rem]">
              Passwörter fürs Team. Verschlüsselt und nachvollziehbar.
            </h1>
            <p className="mt-5 max-w-md text-[0.95rem] leading-relaxed text-mist">
              Einträge werden im Browser verschlüsselt und das Master-Passwort wird nicht übertragen. Der Server speichert nur Chiffretext.
            </p>
            <ul className="mt-9 divide-y divide-line border-y border-line">
              {[
                { icon: ShieldCheck, title: "Zwei Faktoren", text: "Authenticator-Code, Passkeys und Backup-Codes." },
                { icon: Fingerprint, title: "Passkeys", text: "Phishing-resistente Anmeldung mit Gerät oder Sicherheitsschlüssel." },
                { icon: ScrollText, title: "Prüfbares Protokoll", text: "Wer hat was wann geändert – ohne Geheimnisse im Log." }
              ].map((feature): ReactNode => {
                const Icon = feature.icon;
                return (
                  <li key={feature.title} className="flex items-start gap-3.5 py-3.5">
                    <Icon size={17} aria-hidden="true" className="mt-0.5 shrink-0 text-flow" />
                    <span>
                      <span className="block text-sm font-semibold text-foam">{feature.title}</span>
                      <span className="block text-sm text-mist">{feature.text}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          <p className="flex items-center gap-2 text-[0.8125rem] text-mist-2">
            <LockKeyhole size={13} aria-hidden="true" />
            Ein Produkt von FlowDesign · Verschlüsselung im Browser · EU-Hosting
          </p>
        </div>
      </aside>

      <main className="flex items-center justify-center px-5 py-12 sm:px-10">
        <div className="w-full max-w-[24rem]">
          <div className="mb-10 lg:hidden">
            <Logo />
          </div>

          {pending?.kind === "second-factor" ? (
            <div className="animate-fade-up">
              <SecondFactorStep
                ticket={pending.ticket}
                onVerified={async (outcome: SecondFactorOutcome): Promise<void> => {
                  const next = await afterSecondFactor(pending.client, outcome);
                  if (next.kind === "set-password") {
                    setPending({ ...pending, kind: "set-password", ticket: next.ticket, recovered: next.recovered });
                    return;
                  }
                  onAuthenticated(pending.client, pending.email, pending.passwordLength);
                }}
              />
              <div className="mt-5 border-t border-line pt-4">
                <Button variant="ghost" className="w-full" onClick={cancelPending}>
                  Abbrechen
                </Button>
              </div>
            </div>
          ) : pending?.kind === "set-password" ? (
            <div className="animate-fade-up">
              <InitialPasswordStep
                pending={pending}
                minLength={minLength}
                onDone={(length: number): void => onAuthenticated(pending.client, pending.email, length)}
                onCancel={cancelPending}
              />
            </div>
          ) : (
            <div className="animate-fade-up">
              <div className="flex flex-col gap-6">
                <div>
                  <h2 className="font-display text-[1.75rem] font-extrabold tracking-[-0.02em] text-foam">
                    {setupRequired ? "Ersteinrichtung" : "Willkommen zurück"}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-mist">
                    {setupRequired
                      ? "Lege das Administrator-Konto an. Danach ist dieser Schritt für immer geschlossen."
                      : "Melde dich mit deiner E-Mail und deinem Master-Passwort an."}
                  </p>
                </div>

                {idle ? (
                  <Alert tone="info">Der Tresor wurde wegen Inaktivität gesperrt.</Alert>
                ) : null}

                {linking ? (
                  <Alert tone="info">
                    Melde dich an, um die Browser-Erweiterung zu verbinden – am schnellsten mit deinem Passkey.
                  </Alert>
                ) : null}

                {desktop ? <ServerSetting value={server} onSaved={setServer} /> : null}

                {setupRequired ? (
                  <SetupForm
                    serverReady={!desktop || normalizeServerUrl(server) !== undefined}
                    minLength={minLength}
                    onAuthenticated={onAuthenticated}
                  />
                ) : (
                  <LoginForm
                    serverReady={!desktop || normalizeServerUrl(server) !== undefined}
                    onAuthenticated={onAuthenticated}
                    onPending={onPending}
                    preferPasskey={linking}
                  />
                )}
              </div>
            </div>
          )}

          {pending === undefined && !setupRequired && config.data !== undefined ? (
            <p className="mt-8 border-t border-line pt-5 text-[0.8125rem] leading-relaxed text-mist-2">
              Konten werden von deinem Administrator angelegt. Du erhältst ein Einmalpasswort und legst bei der ersten Anmeldung dein eigenes Master-Passwort fest.
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}

/**
 * Desktop only: the API address. Saved on blur so the auth config refetches against the new server.
 * https is enforced (loopback http allowed for development).
 */
function ServerSetting(props: { readonly value: string; readonly onSaved: (value: string) => void }): ReactNode {
  const [draft, setDraft] = useState(props.value);
  const [invalid, setInvalid] = useState(false);

  function commit(): void {
    if (draft.trim() === "") {
      setInvalid(true);
      return;
    }
    const saved = saveServerUrl(draft);
    setInvalid(saved === undefined);
    if (saved !== undefined) {
      setDraft(saved);
      props.onSaved(saved);
    }
  }

  return (
    <Field
      id="login-server"
      label="Server"
      hint={invalid ? undefined : "Adresse deiner PasswordFlow-API, z. B. https://api.firma.de"}
      error={invalid ? "Bitte gib eine https-Adresse ein." : undefined}
    >
      <TextInput
        id="login-server"
        type="url"
        inputMode="url"
        autoComplete="url"
        spellCheck={false}
        value={draft}
        invalid={invalid}
        placeholder="https://api.firma.de"
        onChange={(event): void => {
          setDraft(event.target.value);
          setInvalid(false);
        }}
        onBlur={commit}
        onKeyDown={(event): void => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
        }}
      />
    </Field>
  );
}

interface LoginFormProps {
  readonly serverReady: boolean;
  readonly onAuthenticated: OnAuthenticated;
  readonly onPending: (step: PendingStep) => void;
  readonly preferPasskey?: boolean | undefined;
}

function LoginForm(props: LoginFormProps): ReactNode {
  const preferPasskey = props.preferPasskey === true && passkeyUnlockAvailable();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  function validate(): string | undefined {
    if (!props.serverReady) {
      return "Bitte gib zuerst eine gültige Server-Adresse ein.";
    }
    if (!isValidEmail(email)) {
      return "Bitte gib eine gültige E-Mail-Adresse ein.";
    }
    return password === "" ? "Bitte gib dein Master-Passwort ein." : undefined;
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    const problem = validate();
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    const address = email.trim().toLowerCase();
    const secret = password;
    const client = createClient();
    setBusy(true);
    setStatus("Schlüssel wird abgeleitet …");
    try {
      const step = await signIn(client, address, secret);
      if (step.kind === "authenticated") {
        props.onAuthenticated(client, address, secret.length);
      } else {
        props.onPending({
          kind: step.kind,
          client,
          email: address,
          ticket: step.ticket,
          oneTime: step.kind === "set-password" || step.passwordChange ? secret : "",
          recovered: step.kind === "set-password" && step.recovered,
          passwordLength: secret.length
        });
      }
    } catch (err: unknown) {
      client.terminate();
      setError(describeAuthError(err));
      setStatus(undefined);
    } finally {
      setPassword("");
      setBusy(false);
    }
  }

  async function unlockWithPasskey(): Promise<void> {
    if (busy) {
      return;
    }
    const client = createClient();
    setBusy(true);
    setError(undefined);
    setStatus("Bestätige mit deinem Passkey …");
    try {
      const address = await signInWithPasskey(client);
      props.onAuthenticated(client, address, undefined);
    } catch (err: unknown) {
      client.terminate();
      setError(describeAuthError(err));
      setStatus(undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5" noValidate>
      <Field id="login-email" label="E-Mail">
        <TextInput
          id="login-email"
          type="email"
          autoComplete="username"
          autoFocus
          inputMode="email"
          value={email}
          onChange={(event): void => setEmail(event.target.value)}
          placeholder="name@firma.de"
          invalid={error !== undefined && email.trim() === ""}
          disabled={busy}
        />
      </Field>

      <Field id="login-password" label="Master-Passwort" hint="Bei der ersten Anmeldung: das Einmalpasswort von deinem Administrator.">
        <SecretInput
          id="login-password"
          autoComplete="current-password"
          value={password}
          onChange={(event): void => setPassword(event.target.value)}
          disabled={busy}
          revealLabel="Master-Passwort"
        />
      </Field>

      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      {busy && status !== undefined ? <p className="text-sm text-mist" role="status">{status}</p> : null}

      <Button type="submit" variant={preferPasskey ? "secondary" : "primary"} size="lg" className="mt-1 w-full" loading={busy}>
        Tresor entsperren
      </Button>
      {passkeyUnlockAvailable() ? (
        <>
          <div className="flex items-center gap-3 text-xs text-mist-2" aria-hidden="true">
            <span className="h-px flex-1 bg-line" />
            oder
            <span className="h-px flex-1 bg-line" />
          </div>
          <Button variant={preferPasskey ? "primary" : "secondary"} size="lg" className="w-full" icon={Fingerprint} disabled={busy} onClick={(): void => void unlockWithPasskey()}>
            Mit Passkey entsperren
          </Button>
        </>
      ) : null}
      <p className="text-center text-sm">
        <Link to={paths.recover} className="text-mist underline-offset-4 hover:text-foam hover:underline">
          Master-Passwort vergessen?
        </Link>
      </p>
    </form>
  );
}

function SetupForm(props: { readonly serverReady: boolean; readonly minLength: number; readonly onAuthenticated: OnAuthenticated }): ReactNode {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  function validate(): string | undefined {
    if (!props.serverReady) {
      return "Bitte gib zuerst eine gültige Server-Adresse ein.";
    }
    if (code.trim() === "") {
      return "Bitte gib den Einrichtungscode aus dem Server-Log ein.";
    }
    if (!isValidEmail(email)) {
      return "Bitte gib eine gültige E-Mail-Adresse ein.";
    }
    const weak = masterPasswordProblem(password, confirm, undefined, props.minLength);
    if (weak !== undefined) {
      return weak;
    }
    return consent ? undefined : "Bitte bestätige, dass du die Wiederherstellung verstanden hast.";
  }

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    const problem = validate();
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    const address = email.trim().toLowerCase();
    const client = createClient();
    setBusy(true);
    try {
      await setupAdmin(client, code.trim(), address, password);
      props.onAuthenticated(client, address, password.length);
    } catch (err: unknown) {
      client.terminate();
      setError(describeAuthError(err));
    } finally {
      setPassword("");
      setConfirm("");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5" noValidate>
      <Alert tone="info">
        Der Server hat beim Start einen Einrichtungscode in sein Log geschrieben (z. B. Railway → Deployments → Logs). Nur wer das Log lesen kann, kann diese Instanz einrichten.
      </Alert>
      <Field id="setup-code" label="Einrichtungscode">
        <TextInput
          id="setup-code"
          autoComplete="off"
          spellCheck={false}
          autoFocus
          value={code}
          onChange={(event): void => setCode(event.target.value)}
          placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
          className="font-mono tracking-wider"
          disabled={busy}
        />
      </Field>
      <Field id="setup-email" label="E-Mail des Administrators">
        <TextInput
          id="setup-email"
          type="email"
          autoComplete="username"
          inputMode="email"
          value={email}
          onChange={(event): void => setEmail(event.target.value)}
          placeholder="name@firma.de"
          disabled={busy}
        />
      </Field>
      <NewPasswordFields
        idPrefix="setup"
        minLength={props.minLength}
        password={password}
        confirm={confirm}
        consent={consent}
        busy={busy}
        onPassword={setPassword}
        onConfirm={setConfirm}
        onConsent={setConsent}
      />
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      {busy ? <p className="text-sm text-mist" role="status">Schlüssel werden erzeugt …</p> : null}
      <Button type="submit" size="lg" className="mt-1 w-full" loading={busy} icon={ShieldCheck}>
        Administrator anlegen
      </Button>
    </form>
  );
}

/** First sign-in with a one-time password: the user picks their own master password before anything else. */
function InitialPasswordStep(props: {
  readonly pending: PendingStep;
  readonly minLength: number;
  readonly onDone: (passwordLength: number) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) {
      return;
    }
    const problem = masterPasswordProblem(password, confirm, props.pending.oneTime, props.minLength)
      ?? (consent ? undefined : "Bitte bestätige, dass du die Wiederherstellung verstanden hast.");
    setError(problem);
    if (problem !== undefined) {
      return;
    }
    setBusy(true);
    try {
      await chooseInitialPassword(props.pending.client, props.pending.email, props.pending.ticket, password, props.pending.recovered);
      props.onDone(password.length);
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setPassword("");
      setConfirm("");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event): void => void submit(event)} className="flex flex-col gap-5" noValidate>
      <div>
        <span className="mb-4 grid size-10 place-items-center rounded-lg border border-line bg-ink-2 text-flow">
          <KeyRound size={18} aria-hidden="true" />
        </span>
        <h2 className="font-display text-[1.75rem] font-extrabold tracking-[-0.02em] text-foam">
          {props.pending.recovered ? "Neues Master-Passwort festlegen" : "Eigenes Master-Passwort festlegen"}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-mist">
          {props.pending.recovered
            ? "Ein Administrator hat deinen Zugang wiederhergestellt. Lege jetzt ein neues Master-Passwort fest – deine Einträge bleiben erhalten, und nur du kennst das neue Passwort."
            : "Du hast dich mit einem Einmalpasswort angemeldet. Lege jetzt dein eigenes Master-Passwort fest. Erst danach ist dein Tresor nutzbar – und nur du kennst das neue Passwort."}
        </p>
      </div>
      <p className="rounded-lg border border-line bg-ink-2 px-3.5 py-2.5 text-sm text-mist">
        Konto: <span className="font-medium text-foam">{props.pending.email}</span>
      </p>
      <NewPasswordFields
        idPrefix="initial"
        minLength={props.minLength}
        password={password}
        confirm={confirm}
        consent={consent}
        busy={busy}
        onPassword={setPassword}
        onConfirm={setConfirm}
        onConsent={setConsent}
      />
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      <div className="flex flex-col gap-2">
        <Button type="submit" size="lg" className="w-full" loading={busy}>
          Master-Passwort speichern & entsperren
        </Button>
        <Button variant="ghost" className="w-full" onClick={props.onCancel} disabled={busy}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}
